/**
 * OSMD-`GraphicalNote` → {@link NoteInfo} / music21-Name – der einzige OSMD-abhängige Teil der
 * Noten-Adressierung (die pure XML-Manipulation lebt in `@/composables/musicXmlDom`). Plain
 * functions – kein Composable: hier gibt es kein ref/computed/watch (aufgegangen aus dem
 * früheren Schein-Composable `useNoteEditing`).
 */
import {AccidentalEnum, GraphicalNote, GraphicalVoiceEntry, NoteEnum, Pitch} from 'opensheetmusicdisplay';
import {type NoteInfo, music21NameWithOctave} from '@/composables/musicXmlDom';

// Convert OSMD NoteEnum to note name string
function getNoteNameFromFundamental(noteEnum: NoteEnum): string {
    const noteNames: Record<number, string> = {
        [NoteEnum.C]: 'C',
        [NoteEnum.D]: 'D',
        [NoteEnum.E]: 'E',
        [NoteEnum.F]: 'F',
        [NoteEnum.G]: 'G',
        [NoteEnum.A]: 'A',
        [NoteEnum.B]: 'B',
    };
    return noteNames[noteEnum] || 'C';
}

// Convert OSMD AccidentalEnum to numeric alter value (-1, 0, 1, etc.)
function accidentalEnumToNumber(accidental: AccidentalEnum): number {
    switch (accidental) {
        case AccidentalEnum.DOUBLEFLAT:
            return -2;
        case AccidentalEnum.FLAT:
            return -1;
        case AccidentalEnum.NONE:
        case AccidentalEnum.NATURAL:
            return 0;
        case AccidentalEnum.SHARP:
            return 1;
        case AccidentalEnum.DOUBLESHARP:
            return 2;
        default:
            return 0;
    }
}

/**
 * MusicXML-Oktave aus einem OSMD-`Pitch`.
 *
 * OSMD speichert die Oktave um `Pitch.OctaveXmlDifference` (= 3) versetzt: der MusicXML-Leser
 * rechnet beim Import `octave -= OctaveXmlDifference`, das mittlere C (`<octave>4</octave>`) hat
 * also `pitch.Octave === 1` (und `getHalfTone() === 48`). Gerechnet wird gegen die
 * **Bibliothekskonstante**, nicht gegen ein Literal: ein falscher Summand ist von außen unsichtbar
 * und verschiebt still ALLES, was an der Tonhöhe hängt – Tooltip/Kontextmenü, den Vorschau-Ton der
 * Auswahl und den Namensabgleich des Rot-Layers für unspielbare Noten.
 *
 * Querprobe, die den Wert festnagelt und in `useNoteEditing.spec.ts` als Test steht:
 * `pitchToSemitone(step, alter, musicXmlOctaveOf(pitch))` muss `pitch.getHalfTone()` ergeben –
 * beide zählen C0 = 0.
 */
function musicXmlOctaveOf(pitch: Pitch): number {
    return pitch.Octave + Pitch.OctaveXmlDifference;
}

/** music21-Schreibweise (`"B-3"`, `"F#4"`) der Tonhöhe einer gerenderten Note; `null` bei
 *  Pausen und fehlender Tonhöhe.
 *
 *  Bewusst OHNE die Takt-/Stimmen-Zählung von {@link getNoteInfoFromGraphicalNote}: das
 *  Unspielbar-Highlight ruft dies für JEDE Note der Partitur auf, und jener Weg scannt pro
 *  Aufruf den ganzen Takt neu (quadratische Laufzeit). Die Oktave kommt aus demselben
 *  {@link musicXmlOctaveOf} – an diesem Namen hängt der Abgleich gegen
 *  `AnalysisResult.unmappable_notes` (music21 `nameWithOctave`), er muss also exakt die
 *  MusicXML-Oktave tragen. */
export function music21NameOfGraphicalNote(gNote: GraphicalNote): string | null {
    const sourceNote = gNote?.sourceNote;
    if (!sourceNote || sourceNote.isRest() === true) return null;
    const pitch = sourceNote.Pitch;
    if (!pitch) return null;
    return music21NameWithOctave(
        getNoteNameFromFundamental(pitch.FundamentalNote),
        accidentalEnumToNumber(pitch.Accidental),
        musicXmlOctaveOf(pitch),
    );
}

export function getNoteInfoFromGraphicalNote(gNote: GraphicalNote, _legacyMeasureNumber?: number): NoteInfo | null {
    try {
        const sourceNote = gNote.sourceNote;
        if (!sourceNote) return null;

        const isRest = sourceNote.isRest() === true;
        const pitch = sourceNote.Pitch;

// Find note index within the measure (counting all notes and rests)
// noteIndex = flacher, zeit-verschränkter Index (OSMD-intern, für Highlight/Navigation).
// voiceNoteIndex = Position innerhalb der eigenen Stimme (für den XML-Writeback, der die
// stimmen-sequentielle Dokumentreihenfolge des MusicXML nutzt).
        let noteIndex = 0;
        let voiceNoteIndex = 0;
        let voice = 1;
        let staffIndex = 0;
        let partId = '';
        const measure = gNote.parentVoiceEntry?.parentStaffEntry?.parentMeasure;
        const sourceMeasure = measure?.parentSourceMeasure;
        if (!measure || !sourceMeasure) return null;

        // `measureListIndex` ist der kanonische, eindeutige Taktordinalindex. Die gedruckte
        // MusicXML-Nummer bleibt separat für Editor-Verträge; GraphicalMeasure.MeasureNumber ist
        // ein OSMD-interner Zähler und kann bei Pickup/Start 0 vs. 1 abweichen.
        const measureIndex = sourceMeasure.measureListIndex;
        const measureNumber = Number.isFinite(sourceMeasure.MeasureNumberXML)
            ? sourceMeasure.MeasureNumberXML
            : sourceMeasure.getPrintedMeasureNumber();

        // Notenzeile + Part der Note (mehrstimmige Partituren): jede Notenzeile hat eine eigene
        // GraphicalMeasure desselben SourceMeasure → hierüber disambiguieren.
        staffIndex = measure.ParentStaff?.idInMusicSheet ?? 0;
        partId = measure.ParentStaff?.ParentInstrument?.IdString ?? '';
        let index = 0;
        const perVoiceCount: Record<number, number> = {};
        let found = false;

        for (const staffEntry of measure.staffEntries || []) {
            if (found) break;
            for (const voiceEntry of staffEntry.graphicalVoiceEntries || []) {
                if (found) break;
                const voiceId = (voiceEntry as GraphicalVoiceEntry).parentVoiceEntry?.ParentVoice.VoiceId ?? 1;
                for (const note of voiceEntry.notes || []) {
                    if (note.sourceNote === sourceNote) {
                        noteIndex = index;
                        voice = voiceId;
                        voiceNoteIndex = perVoiceCount[voiceId] ?? 0;
                        found = true;
                        break;
                    }
                    perVoiceCount[voiceId] = (perVoiceCount[voiceId] ?? 0) + 1;
                    index++;
                }
            }
        }
        if (!found) return null;

// Tonhöhe; bei Pausen bleibt step leer – das ist das Signal, octave/alter sind dort Platzhalter.
        let step = '';
        let octave = 4;
        let alter = 0;

        if (!isRest && pitch) {
// Tonhöhe direkt aus OSMDs Pitch; die Oktave über musicXmlOctaveOf in die MusicXML-Zählung
// (C4 = mittleres C), in der alle Konsumenten von NoteInfo rechnen.
            step = getNoteNameFromFundamental(pitch.FundamentalNote);
            octave = musicXmlOctaveOf(pitch);
            alter = accidentalEnumToNumber(pitch.Accidental);
        }
        return {
            frequency: !isRest && pitch ? pitch.Frequency : 0,
            semitones: !isRest && pitch ? pitch.getHalfTone() : 0,

            measureNumber,
            measureIndex,
            noteIndex,
            voiceNoteIndex,
            staffIndex,
            partId,
            step,
            alter,
            octave,
            duration: gNote?.graphicalNoteLength?.toString() || '',
            voice,
            isRest,
        };
    } catch (e) {
        console.error('Error extracting note info:', e);
        return null;
    }
}
